import React, { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { errorText, hasPendingFlow } from '../../api/auth'
import Ikona from './IkonaZlecenia'

/** Puste dane konta gościa w oknie „Wystaw zlecenie”. */
export const PUSTE_KONTO = { email: '', haslo: '', haslo2: '', regulamin: false, marketing: false }

/** Braki konta gościa przed wysyłką: `{ pole: komunikat }`, puste, gdy wszystko jest. */
export const brakiKonta = (k) => {
  const b = {}
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(k.email.trim())) b.email = 'Podaj adres e-mail.'
  if (k.haslo.length < 8) b.haslo = 'Hasło musi mieć co najmniej 8 znaków.'
  else if (k.haslo2 !== k.haslo) b.haslo2 = 'Hasła się różnią.'
  if (!k.regulamin) b.regulamin = 'Zaakceptuj regulamin, żeby założyć konto.'
  return b
}

const Blad = ({ children }) => (
  <p className="blad" role="alert"><Ikona nazwa="uwaga" rozmiar={16} grubosc={2.2} />{children}</p>
)

/**
 * „Twoje konto” w oknie „Wystaw zlecenie” dla gościa (decyzja właściciela 2026-10-26). Dwa tryby:
 * - `nowe`: e-mail, hasło, powtórz hasło i regulamin; zlecenie idzie razem z kontem (`POST /ogloszenia/z-kontem`),
 *   a jeden mail potwierdza konto i publikuje zlecenie;
 * - `logowanie`: „Masz konto? Zaloguj się” w tym samym oknie, potem zwykła wysyłka (`onZalogowano`).
 * Stan konta trzyma formularz (`konto`, `onZmiana`), bo wysyłka jest tam; `bledy` to braki i błędy z backendu przy polach.
 */
const KontoGoscia = ({ konto, onZmiana, bledy = {}, tryb, onTryb, komunikat = '', onZalogowano }) => {
  const id = useId()
  const { login } = useAuth()
  const [haslo, setHaslo] = useState('')
  const [logowanie, setLogowanie] = useState({ busy: false, blad: '' })
  const ustaw = (pole, wartosc) => onZmiana({ ...konto, [pole]: wartosc })

  const zaloguj = async () => {
    if (!konto.email.trim() || !haslo) { setLogowanie({ busy: false, blad: 'Podaj e-mail i hasło.' }); return }
    setLogowanie({ busy: true, blad: '' })
    const res = await login({ email: konto.email.trim(), password: haslo })
    if (res.meta?.is_authenticated) { setLogowanie({ busy: false, blad: '' }); onZalogowano?.(); return }
    setLogowanie({
      busy: false,
      blad: hasPendingFlow(res, 'verify_email')
        ? 'To konto czeka na potwierdzenie e-maila. Kliknij link z wiadomości od nas, a potem zaloguj się tutaj.'
        : errorText(res, 'Nie udało się zalogować.'),
    })
  }

  return (
    <fieldset className="konto-goscia">
      <legend>Twoje konto</legend>
      {komunikat && <p className="pole-podpowiedz" role="status">{komunikat}</p>}
      <label className="pole" htmlFor={`${id}-e`}>E-mail
        <input className="wejscie" id={`${id}-e`} type="email" autoComplete="email" value={konto.email}
               onChange={(e) => ustaw('email', e.target.value)} aria-invalid={Boolean(bledy.email)} />
      </label>
      {bledy.email && <Blad>{bledy.email}</Blad>}

      {tryb === 'logowanie' ? (
        <>
          <label className="pole" htmlFor={`${id}-l`}>Hasło
            <input className="wejscie" id={`${id}-l`} type="password" autoComplete="current-password" value={haslo}
                   onChange={(e) => setHaslo(e.target.value)} />
          </label>
          {logowanie.blad && <Blad>{logowanie.blad}</Blad>}
          <div className="konto-goscia-rzad">
            <button type="button" className="btn btn-obrys btn-m" onClick={zaloguj} disabled={logowanie.busy}>
              {logowanie.busy ? 'Loguję…' : 'Zaloguj się'}
            </button>
            <Link to="/reset-hasla" target="_blank" rel="noopener">Nie pamiętam hasła</Link>
          </div>
          <p className="pole-podpowiedz">
            Nie masz konta? <button type="button" className="link" onClick={() => onTryb('nowe')}>Załóż je tutaj</button>
          </p>
        </>
      ) : (
        <>
          <div className="pola">
            <label className="pole" htmlFor={`${id}-h`}>Hasło
              <input className="wejscie" id={`${id}-h`} type="password" autoComplete="new-password" value={konto.haslo}
                     onChange={(e) => ustaw('haslo', e.target.value)} aria-invalid={Boolean(bledy.haslo)} />
            </label>
            <label className="pole" htmlFor={`${id}-h2`}>Powtórz hasło
              <input className="wejscie" id={`${id}-h2`} type="password" autoComplete="new-password" value={konto.haslo2}
                     onChange={(e) => ustaw('haslo2', e.target.value)} aria-invalid={Boolean(bledy.haslo2)} />
            </label>
          </div>
          {bledy.haslo && <Blad>{bledy.haslo}</Blad>}
          {bledy.haslo2 && <Blad>{bledy.haslo2}</Blad>}
          <label className="zgoda">
            <input type="checkbox" checked={konto.regulamin} onChange={(e) => ustaw('regulamin', e.target.checked)}
                   aria-invalid={Boolean(bledy.regulamin)} />
            <span>
              Akceptuję <Link to="/regulamin" target="_blank" rel="noopener">regulamin</Link> i{' '}
              <Link to="/polityka-prywatnosci" target="_blank" rel="noopener">politykę prywatności</Link>.
            </span>
          </label>
          {bledy.regulamin && <Blad>{bledy.regulamin}</Blad>}
          {/* Zgody rozdzielone jak przy rejestracji: regulamin jest warunkiem konta, marketing dobrowolny. */}
          <label className="zgoda">
            <input type="checkbox" checked={konto.marketing} onChange={(e) => ustaw('marketing', e.target.checked)} />
            <span>Chcę dostawać informacje o nowościach i promocjach <small>(opcjonalnie)</small></span>
          </label>
          <p className="pole-podpowiedz">
            Wyślemy Ci link. Po kliknięciu konto będzie gotowe, a zlecenie trafi do wykonawców.
          </p>
          <p className="pole-podpowiedz">
            Masz konto? <button type="button" className="link" onClick={() => onTryb('logowanie')}>Zaloguj się</button>
          </p>
        </>
      )}
    </fieldset>
  )
}

export default KontoGoscia
